import React, { useMemo, useState } from 'react';
import {
    Form,
    FormFeedback,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    Nav,
    NavItem,
    NavLink,
    Spinner,
    TabContent,
    TabPane,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { confirmImportPublishers, importPublishers } from '../../api/publisherApi';
import TableContainer from '../.././components/TableContainerReactTable';
import { cnpjMask } from '../../utils';
import { Publisher } from '../../types/publisher';

interface ImportPlacementModalProps {
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Publisher[]) => void;
}

const ImportPlacementModal = ({ isOpen, toggle, onAddData }: ImportPlacementModalProps) => {
    const [activeArrowTab, setActiveArrowTab] = useState<number>(1);
    const [firstStepImportButtonLoader, setFirstStepImportButtonLoader] = useState<boolean>(false);
    const [secondStepImportButtonLoader, setSecondStepImportButtonLoader] = useState<boolean>(false);
    const [publishersToSave, setPublishersToSave] = useState<(Publisher & { errors: string[] })[]>([]);

    const toggleImportPublisherModal = () => {
        toggle();
        setActiveArrowTab(1);
        handleFirstStepForm.resetForm();
        setPublishersToSave([]);
    };

    const publishersToAddColumns = useMemo(
        () => [
            {
                header: 'Mídia Cad',
                accessorKey: 'mediaCad',
                enableColumnFilter: false,

            },
            {
                header: 'Nome',
                accessorKey: 'name',
                enableColumnFilter: false,
            },
            {
                header: 'CNPJ',
                accessorFn: (row: any) => {
                    return row.cnpj ? cnpjMask(row.cnpj) : '';
                },
                enableColumnFilter: false,
            },
            {
                header: 'Site',
                accessorKey: 'site',
                enableColumnFilter: false,
            },
            {
                header: 'App',
                accessorKey: 'app',
                enableColumnFilter: false,
            },
            {
                header: 'Mídia Kit',
                accessorFn: (row: any) => {
                    return row.mediaKitUrl || row.mediaKitFile;
                },
                cell: (cell: any) => {
                    const cellValue = cell.getValue();
                    if (cellValue) {
                        return (<>{cellValue} <a
                            href={cellValue}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <i className="ri-external-link-line"></i>
                        </a></>);
                    }

                    return '-';

                },
                enableColumnFilter: false,
            },
            {
                header: 'Recursos de acessibilidade',
                accessorKey: 'accessibilityResources',
                enableColumnFilter: false,
            },
            {
                header: 'Erros',
                accessorFn: (row: any) => {
                    return row.errors.length === 0 ? '0' : row.errors.join(';\n');
                },
                enableColumnFilter: false,
            },

        ],
        [],
    );

    const handleFirstStepForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            file: undefined,
        },

        validationSchema: Yup.object({
            file: Yup.mixed().required('É necessário um arquivo csv'),
            // TODO: As validações abaixo não estão funcionando
            // .test(
            //     "fileFormat",
            //     "Placement de arquivo inválido",
            //     (value: any) => value && SUPPORTED_FORMATS.includes(value.type)
            // )
            // .test(
            //     "fileSize",
            //     "O arquivo é muito grande",
            //     (value: any) => value && value.size <= 2000000
            // ),
        }),
        onSubmit: () => {
            setFirstStepImportButtonLoader(true);
            const formData = new FormData();
            const file: HTMLInputElement | null = document.querySelector('#file-field');
            if (file && file.files) {
                formData.append('file', file.files[0]);
            }
            return importPublishers(formData)
                .then((res: any) => {
                    setPublishersToSave(res);
                    setActiveArrowTab(2);
                })
                .catch(() => {})
                .finally(() => {
                    setFirstStepImportButtonLoader(false);
                });
        },
    });

    // Second Step Form Validation
    const handleSecondStepForm = (event: any) => {
        event.preventDefault();
        const publishersToSaveCount = publishersToSave.length;
        if (!window.confirm(`Deseja realmente importar ${publishersToSaveCount} publishers?`)) {
            return;
        }

        const countWarnings = publishersToSave.filter((publisher) => publisher.errors.length > 0).length;
        if (countWarnings) {
            if (!window.confirm(`Existem ${countWarnings} publishers com alertas. Deseja seguir mesmo assim?`)) {
                return;
            }
        }
        const countErrors = publishersToSave.filter((publisher) => publisher.errors.length > 0).length;
        if (countErrors) {
            window.alert(`Existem ${countErrors} publishers com erros. Não é possível importar publishers com erros.`);
            return;
        }

        setSecondStepImportButtonLoader(true);

        confirmImportPublishers(publishersToSave)
        .then((res: any) => {
            toggleImportPublisherModal();
            toast.success('Publishers importados com sucesso!');
            onAddData(res);
        })
        .catch(() => {})
        .finally(() => {
            setSecondStepImportButtonLoader(false);
        });
    };

    return (
        <Modal id="showModal" isOpen={isOpen} toggle={toggleImportPublisherModal} fade={false} keyboard={true}
               backdrop={false} centered size={'xl'}>
            <ModalHeader toggle={toggleImportPublisherModal} className="bg-light p-3">
                Importar publishers
            </ModalHeader>

            <ModalBody>
                <div className="container-fluid">
                    <div className="row">
                        <div className="step-arrow-nav mb-4">
                            <Nav
                                className="nav-pills custom-nav nav-justified"
                                role="tablist"
                            >
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        id="steparrow-gen-info-tab"
                                        className={`${activeArrowTab === 1 ? 'active' : ''} ${activeArrowTab >= 2 ? 'done' : ''}`}
                                        onClick={() => {
                                            if (activeArrowTab === 2) {
                                                setActiveArrowTab(1);
                                            }
                                        }}
                                    >
                                        Importe
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        id="steparrow-gen-info-tab"
                                        className={`${activeArrowTab === 2 ? 'active' : ''} ${activeArrowTab >= 3 ? 'done' : ''}`}
                                    >
                                        Confirme
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        id="steparrow-gen-info-tab"
                                        className={activeArrowTab === 3 ? 'active' : ''}
                                    >
                                        Concluído
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </div>
                    </div>
                    <div className="row">
                        <TabContent activeTab={activeArrowTab}>
                            <TabPane id="step-1" tabId={1}>
                                <Form onSubmit={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleFirstStepForm.handleSubmit();
                                    return false;
                                }}>
                                    <div>
                                        <div className="mb-3">
                                            <Label
                                                htmlFor="file-field"
                                                className="form-label"
                                            >
                                                Publishers
                                            </Label>
                                            <Input
                                                className="form-control"
                                                type="file"
                                                id="file-field"
                                                name="file"
                                                accept=".csv"
                                                onChange={handleFirstStepForm.handleChange}
                                                onBlur={handleFirstStepForm.handleBlur}
                                                value={handleFirstStepForm.values.file}
                                                invalid={
                                                    !!(handleFirstStepForm.touched.file && handleFirstStepForm.errors.file)
                                                }
                                            />
                                            {handleFirstStepForm.touched.file && handleFirstStepForm.errors.file ? (
                                                <FormFeedback
                                                    type="invalid">{handleFirstStepForm.errors.file}</FormFeedback>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-start gap-3 mt-4">
                                        <button
                                            type="submit"
                                            disabled={firstStepImportButtonLoader}
                                            className="btn btn-success btn-label right ms-auto nexttab nexttab"
                                        >
                                            <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                                            {firstStepImportButtonLoader && <Spinner size="sm"
                                                                                     className="me-2"> Carregando... </Spinner>}
                                            Importar
                                        </button>
                                    </div>
                                </Form>
                            </TabPane>

                            <TabPane id="step-2" tabId={2}>
                                <TableContainer
                                    divClass="table-responsive"
                                    columns={(publishersToAddColumns || [])}
                                    data={(publishersToSave || [])}
                                    isGlobalFilter={true}
                                    customPageSize={30}
                                    SearchPlaceholder="Procurar..."
                                />

                                <div className="d-flex align-items-start gap-3 mt-4">
                                    <button
                                        type="button"
                                        className="btn btn-light btn-label previestab"
                                        onClick={() => {
                                            setActiveArrowTab(activeArrowTab - 1);
                                        }}
                                    >
                                        <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2"></i>
                                        Cancelar & Voltar
                                    </button>
                                    <button
                                        type="button"
                                        disabled={secondStepImportButtonLoader}
                                        onClick={handleSecondStepForm}
                                        className="btn btn-success btn-label right ms-auto nexttab nexttab"
                                    >
                                        <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                                        {secondStepImportButtonLoader && <Spinner size="sm"
                                                                                  className="me-2"> Carregando... </Spinner>}
                                        Confirmar
                                    </button>
                                </div>
                            </TabPane>

                            <TabPane id="step-3" tabId={3}>
                                <div className="text-center">
                                    <div className="avatar-md mt-5 mb-4 mx-auto">
                                        <div
                                            className="avatar-title bg-light text-success display-4 rounded-circle">
                                            <i className="ri-checkbox-circle-fill"></i>
                                        </div>
                                    </div>
                                    <h5>Muito bom !</h5>
                                    <p className="text-muted">
                                        Seus publishers foram importados com sucesso.
                                    </p>
                                    <p>&nbsp;</p>
                                    <button
                                        type="button"
                                        className="btn btn-success btn-label right ms-auto nexttab nexttab"
                                        onClick={toggleImportPublisherModal}
                                    >
                                        <i className="ri-close-line label-icon align-middle fs-16 ms-2"></i>
                                        Fechar
                                    </button>
                                </div>
                            </TabPane>
                        </TabContent>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default ImportPlacementModal;